import { useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './primitives/Button'
import { useCreateRecurrence } from '../api/hooks'
import { useDeleteTodo } from '../api/hooks'
import { recurrenceId } from '../lib/ulid'
import { todayStr } from '../lib/parse'
import type { Todo, RecurrenceFrequency } from '../api/types'

interface RecurrencePickerProps {
  open: boolean
  onClose(): void
  /** When set, the picker prefills from this todo and the source todo is
   * deleted on submit (the rule replaces it; first occurrence regenerates as
   * a virtual instance starting from the picked start_date). */
  sourceTodo?: Todo
}

const WEEKDAYS = [
  { i: 0, label: 'Sun' },
  { i: 1, label: 'Mon' },
  { i: 2, label: 'Tue' },
  { i: 3, label: 'Wed' },
  { i: 4, label: 'Thu' },
  { i: 5, label: 'Fri' },
  { i: 6, label: 'Sat' },
] as const

export function RecurrencePicker({ open, onClose, sourceTodo }: RecurrencePickerProps) {
  const createRec = useCreateRecurrence()
  const deleteTodo = useDeleteTodo()

  // True when the most recent mousedown event targeted the backdrop itself
  // (not a child). Used to suppress overlay onClick when a drag that started
  // inside the panel happens to release over the backdrop.
  const backdropMouseDownRef = useRef(false)

  const [frequency, setFrequency] = useState<RecurrenceFrequency>('weekly')
  const [startDate, setStartDate] = useState<string>(sourceTodo?.date || todayStr())
  const [endDate, setEndDate] = useState<string>('')
  // Wall-clock time in the user's timezone, applied to every occurrence.
  const [reminderTime, setReminderTime] = useState<string>('')
  const [interval, setInterval] = useState<number>(1)
  const [byweekday, setByweekday] = useState<Set<number>>(() => {
    if (sourceTodo?.date) {
      return new Set([new Date(sourceTodo.date + 'T00:00:00').getDay()])
    }
    return new Set([new Date().getDay()])
  })
  const [bymonthday, setBymonthday] = useState<number>(() => {
    if (sourceTodo?.date) return new Date(sourceTodo.date + 'T00:00:00').getDate()
    return new Date().getDate()
  })

  // Re-prime defaults when sourceTodo changes (e.g. picker reopened for a different todo).
  useEffect(() => {
    if (!open) return
    if (sourceTodo?.date) {
      setStartDate(sourceTodo.date)
      const d = new Date(sourceTodo.date + 'T00:00:00')
      setByweekday(new Set([d.getDay()]))
      setBymonthday(d.getDate())
    }
  }, [open, sourceTodo])

  // Escape closes the modal. Listening on capture phase + window so the
  // handler fires before any inner stopPropagation runs (typing in an input
  // would normally swallow keydown).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  const toggleWeekday = useCallback((i: number) => {
    setByweekday((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }, [])

  const handleSubmit = useCallback(() => {
    if (!sourceTodo) return
    if (frequency === 'weekly' && byweekday.size === 0) {
      // Block submit with no weekday selected — would produce an empty rule.
      return
    }
    createRec.mutate(
      {
        id: recurrenceId(),
        text: sourceTodo.text,
        start_date: startDate,
        end_date: endDate || null,
        reminder_time: reminderTime || null,
        frequency,
        interval_n: frequency === 'every_n_days' || frequency === 'weekly' || frequency === 'monthly' ? interval : 1,
        byweekday: frequency === 'weekly' ? Array.from(byweekday).sort().join(',') : null,
        bymonthday: frequency === 'monthly' ? bymonthday : null,
        section_id: sourceTodo.section_id ?? null,
        note: sourceTodo.note ?? null,
        priority: sourceTodo.priority ?? null,
      },
      {
        onSuccess: () => {
          // The source todo becomes redundant — its first occurrence is now
          // generated by the rule. Soft-delete it.
          deleteTodo.mutate(sourceTodo.id)
          onClose()
        },
      },
    )
  }, [sourceTodo, frequency, startDate, endDate, reminderTime, interval, byweekday, bymonthday, createRec, deleteTodo, onClose])

  if (!open) return null

  return createPortal(
    <div
      // Close only when a click STARTS and ENDS on the backdrop itself.
      // Without this, drag-selecting text inside the modal — which often
      // releases over the backdrop — fires onClick on the overlay and closes
      // the dialog. Tracking mousedown lets us ignore drags that originate
      // inside the panel.
      onMouseDown={(e) => {
        backdropMouseDownRef.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropMouseDownRef.current) {
          onClose()
        }
        backdropMouseDownRef.current = false
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        // Keep events inside the panel from bubbling up to the backdrop or
        // anything mounted behind it (the underlying TodoItem listens for
        // pointerdown / keydown to start drags or delete).
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md, 8px)',
          padding: '20px',
          width: 'min(440px, calc(100vw - 32px))',
          fontFamily: 'var(--font-serif)',
          color: 'var(--color-ink)',
        }}
      >
        <h3 style={{ margin: 0, marginBottom: '16px', fontSize: '1rem' }}>
          Make recurring
        </h3>
        {sourceTodo && (
          <div
            style={{
              fontStyle: 'italic',
              color: 'var(--color-ink-muted)',
              marginBottom: '14px',
              fontSize: '0.875rem',
            }}
          >
            “{sourceTodo.text}”
          </div>
        )}

        <Field label="Repeats">
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
            style={inputStyle}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly on…</option>
            <option value="every_n_days">Every N days</option>
            <option value="monthly">Monthly on day…</option>
          </select>
        </Field>

        {frequency === 'every_n_days' && (
          <Field label="Every (days)">
            <input
              type="number"
              min={1}
              max={365}
              value={interval}
              onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
              style={inputStyle}
            />
          </Field>
        )}

        {frequency === 'weekly' && (
          <>
            <Field label="On weekdays">
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {WEEKDAYS.map((w) => {
                  const on = byweekday.has(w.i)
                  return (
                    <button
                      key={w.i}
                      type="button"
                      onClick={() => toggleWeekday(w.i)}
                      style={{
                        padding: '4px 10px',
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.75rem',
                        background: on ? 'var(--color-accent)' : 'transparent',
                        color: on ? 'var(--color-paper)' : 'var(--color-ink-muted)',
                        border: '1px solid ' + (on ? 'var(--color-accent)' : 'var(--color-border)'),
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      {w.label}
                    </button>
                  )
                })}
              </div>
            </Field>
            <Field label="Every (weeks)">
              <input
                type="number"
                min={1}
                max={52}
                value={interval}
                onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
                style={inputStyle}
              />
            </Field>
          </>
        )}

        {frequency === 'monthly' && (
          <>
            <Field label="Day of month">
              <input
                type="number"
                min={1}
                max={31}
                value={bymonthday}
                onChange={(e) => setBymonthday(Math.max(1, Math.min(31, Number(e.target.value) || 1)))}
                style={inputStyle}
              />
            </Field>
            <Field label="Every (months)">
              <input
                type="number"
                min={1}
                max={24}
                value={interval}
                onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
                style={inputStyle}
              />
            </Field>
          </>
        )}

        <Field label="Starts">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Ends (optional)">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Remind at (optional)">
          <input
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={createRec.isPending || deleteTodo.isPending}
          >
            Create
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--color-border)',
  borderRadius: '3px',
  background: 'var(--color-paper)',
  color: 'var(--color-ink)',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.875rem',
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label
        style={{
          display: 'block',
          fontSize: '0.75rem',
          color: 'var(--color-ink-muted)',
          marginBottom: '4px',
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  )
}
